<script setup>
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import Avatar from 'dashboard/components-next/avatar/Avatar.vue';
import { emitter } from 'shared/helpers/mitt';
import { WABA_CALLING_BUS_EVENT } from './constants';
import { useWabaCallingStore } from './store';

const { t } = useI18n();
const callingStore = useWabaCallingStore();
const incoming = computed(() => callingStore.incoming);
const active = computed(() => callingStore.active);
const visible = computed(() => active.value || incoming.value.length > 0);
const primarySession = computed(() => active.value || incoming.value[0]);
const isOutboundPending = computed(
  () =>
    active.value?.direction === 'business_initiated' &&
    active.value?.state !== 'in_progress'
);

const sessionLabel = session => {
  const remoteNumber =
    session?.direction === 'business_initiated'
      ? session?.to_number
      : session?.from_number;

  return (
    remoteNumber ||
    session?.from_number ||
    session?.to_number ||
    t('ASTRACHAT_WABA_CALLING.WIDGET.UNKNOWN_CONTACT')
  );
};

const onRealtime = ({ event, data }) => {
  callingStore.handleRealtime({ event, data });
};

const accept = async session => {
  const result = await callingStore.accept(session);
  if (result.error) useAlert(t('ASTRACHAT_WABA_CALLING.ALERT.CALL_FAILED'));
};

const decline = session => callingStore.end(session, 'reject');
const end = () => callingStore.end(active.value);

const WIDGET_POSITION_KEY = 'astrachat:waba-calling:widget-position';
const WIDGET_WIDTH = 320; // w-80 = 20rem
const WIDGET_HEIGHT = 120; // altura mínima estimada
const VIEWPORT_PADDING = 8;

const defaultPosition = () => ({
  x: window.innerWidth - WIDGET_WIDTH - 16,
  y: window.innerHeight - WIDGET_HEIGHT - 16,
});

const constrainPosition = (x, y) => ({
  x: Math.max(
    VIEWPORT_PADDING,
    Math.min(x, window.innerWidth - WIDGET_WIDTH - VIEWPORT_PADDING)
  ),
  y: Math.max(
    VIEWPORT_PADDING,
    Math.min(y, window.innerHeight - WIDGET_HEIGHT - VIEWPORT_PADDING)
  ),
});

const loadPosition = () => {
  try {
    const saved = localStorage.getItem(WIDGET_POSITION_KEY);
    if (saved) {
      const parsed = JSON.parse(saved);
      return constrainPosition(parsed.x, parsed.y);
    }
  } catch (e) {
    // ignore corrupted localStorage data
  }
  return defaultPosition();
};

const widgetPosition = ref(loadPosition());
const isDragging = ref(false);
const dragOffset = ref({ x: 0, y: 0 });

const savePosition = () => {
  try {
    localStorage.setItem(
      WIDGET_POSITION_KEY,
      JSON.stringify(widgetPosition.value)
    );
  } catch (e) {
    // ignore private-browsing/localStorage errors
  }
};

const onDrag = event => {
  if (!isDragging.value) return;
  event.preventDefault();

  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const clientY = event.touches ? event.touches[0].clientY : event.clientY;

  widgetPosition.value = constrainPosition(
    clientX - dragOffset.value.x,
    clientY - dragOffset.value.y
  );
};

const stopDrag = () => {
  if (!isDragging.value) return;
  isDragging.value = false;
  savePosition();

  document.removeEventListener('mousemove', onDrag);
  document.removeEventListener('mouseup', stopDrag);
  document.removeEventListener('touchmove', onDrag);
  document.removeEventListener('touchend', stopDrag);
};

const startDrag = event => {
  const clientX = event.touches ? event.touches[0].clientX : event.clientX;
  const clientY = event.touches ? event.touches[0].clientY : event.clientY;

  isDragging.value = true;
  dragOffset.value = {
    x: clientX - widgetPosition.value.x,
    y: clientY - widgetPosition.value.y,
  };

  document.addEventListener('mousemove', onDrag);
  document.addEventListener('mouseup', stopDrag);
  document.addEventListener('touchmove', onDrag, { passive: false });
  document.addEventListener('touchend', stopDrag);
};

const onResize = () => {
  widgetPosition.value = constrainPosition(
    widgetPosition.value.x,
    widgetPosition.value.y
  );
};

onMounted(() => {
  emitter.on(WABA_CALLING_BUS_EVENT, onRealtime);
  window.addEventListener('resize', onResize);
});
onBeforeUnmount(() => {
  emitter.off(WABA_CALLING_BUS_EVENT, onRealtime);
  callingStore.stopIncomingRingtone();
  callingStore.stopOutgoingRingtone();
  stopDrag();
  window.removeEventListener('resize', onResize);
});
</script>

<template>
  <Teleport to="body">
    <div
      v-if="visible"
      class="fixed z-[60] flex w-80 flex-col gap-2"
      :class="{ 'select-none': isDragging }"
      :style="{
        left: `${widgetPosition.x}px`,
        top: `${widgetPosition.y}px`,
      }"
    >
      <div
        v-for="session in active ? incoming : []"
        :key="session.id"
        class="flex items-center gap-3 rounded-xl bg-n-solid-2 p-4 shadow-xl outline outline-1 outline-n-strong"
      >
        <div
          class="inline-flex rounded-full ring-2 ring-n-teal-9 animate-pulse"
        >
          <Avatar :name="sessionLabel(session)" :size="40" rounded-full />
        </div>
        <div class="min-w-0 flex-1">
          <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
            {{ sessionLabel(session) }}
          </p>
          <p class="truncate text-xs text-n-slate-11">
            {{ t('ASTRACHAT_WABA_CALLING.WIDGET.INCOMING') }}
          </p>
        </div>
        <div class="flex shrink-0 gap-2">
          <button
            class="flex size-10 items-center justify-center rounded-full bg-n-ruby-9 transition-colors hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-60"
            :aria-label="t('ASTRACHAT_WABA_CALLING.ACTION.DECLINE')"
            :title="t('ASTRACHAT_WABA_CALLING.ACTION.DECLINE')"
            :disabled="callingStore.busy"
            @click="decline(session)"
          >
            <i class="i-lucide-phone-off text-lg text-white" />
          </button>
          <button
            class="flex size-10 items-center justify-center rounded-full bg-n-teal-9 transition-colors hover:bg-n-teal-10 disabled:cursor-not-allowed disabled:opacity-60"
            :aria-label="t('ASTRACHAT_WABA_CALLING.ACTION.ACCEPT')"
            :title="t('ASTRACHAT_WABA_CALLING.ACTION.ACCEPT')"
            :disabled="callingStore.busy"
            @click="accept(session)"
          >
            <i
              class="text-lg text-white"
              :class="
                callingStore.busy
                  ? 'i-lucide-loader-circle animate-spin'
                  : 'i-lucide-phone'
              "
            />
          </button>
        </div>
      </div>

      <div
        v-if="primarySession"
        class="relative flex items-center gap-3 rounded-xl bg-n-solid-2 p-4 shadow-xl outline outline-1 outline-n-strong"
      >
        <div
          class="absolute -top-3 left-1/2 flex -translate-x-1/2 cursor-grab items-center justify-center rounded-full bg-n-solid-3 px-2 py-1 shadow-md outline outline-1 outline-n-weak transition-colors hover:bg-n-solid-2"
          :class="{ 'cursor-grabbing': isDragging }"
          :title="t('ASTRACHAT_WABA_CALLING.WIDGET.DRAG_HANDLE')"
          @mousedown="startDrag"
          @touchstart="startDrag"
        >
          <i class="i-lucide-grip-vertical text-n-slate-11" />
        </div>
        <div
          class="inline-flex rounded-full ring-2 ring-n-teal-9"
          :class="{ 'animate-pulse': !active }"
        >
          <Avatar
            :name="sessionLabel(primarySession)"
            :size="40"
            rounded-full
          />
        </div>
        <div class="min-w-0 flex-1">
          <p class="mb-0 truncate text-sm font-medium text-n-slate-12">
            {{ sessionLabel(primarySession) }}
          </p>
          <p class="truncate text-xs text-n-slate-11">
            <template v-if="!active">
              {{ t('ASTRACHAT_WABA_CALLING.WIDGET.INCOMING') }}
            </template>
            <template v-else-if="isOutboundPending">
              {{ t('ASTRACHAT_WABA_CALLING.WIDGET.OUTGOING') }}
            </template>
            <template v-else>
              {{ t('ASTRACHAT_WABA_CALLING.WIDGET.IN_CALL') }}
            </template>
          </p>
        </div>
        <div class="flex shrink-0 gap-2">
          <template v-if="active">
            <button
              class="flex size-10 items-center justify-center rounded-full bg-n-ruby-9 transition-colors hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-60"
              :aria-label="t('ASTRACHAT_WABA_CALLING.ACTION.END')"
              :title="t('ASTRACHAT_WABA_CALLING.ACTION.END')"
              :disabled="callingStore.busy"
              @click="end"
            >
              <i class="i-lucide-phone-off text-lg text-white" />
            </button>
          </template>
          <template v-else>
            <button
              class="flex size-10 items-center justify-center rounded-full bg-n-ruby-9 transition-colors hover:bg-n-ruby-10 disabled:cursor-not-allowed disabled:opacity-60"
              :aria-label="t('ASTRACHAT_WABA_CALLING.ACTION.DECLINE')"
              :title="t('ASTRACHAT_WABA_CALLING.ACTION.DECLINE')"
              :disabled="callingStore.busy"
              @click="decline(primarySession)"
            >
              <i class="i-lucide-phone-off text-lg text-white" />
            </button>
            <button
              class="flex size-10 items-center justify-center rounded-full bg-n-teal-9 transition-colors hover:bg-n-teal-10 disabled:cursor-not-allowed disabled:opacity-60"
              :aria-label="t('ASTRACHAT_WABA_CALLING.ACTION.ACCEPT')"
              :title="t('ASTRACHAT_WABA_CALLING.ACTION.ACCEPT')"
              :disabled="callingStore.busy"
              @click="accept(primarySession)"
            >
              <i
                class="text-lg text-white"
                :class="
                  callingStore.busy
                    ? 'i-lucide-loader-circle animate-spin'
                    : 'i-lucide-phone'
                "
              />
            </button>
          </template>
        </div>
      </div>
    </div>
  </Teleport>
</template>
